import React from "react";
import { useSelector } from "react-redux";
import { Box, Card, FlyoutModal } from "@bca/ui-library";
import styled from "styled-components";
import css from "@styled-system/css";
import { CardProps } from "@bca/ui-library/dist/Card/card.type";

import ImageContainer from "./ImageContainer";
import DetailsContainer from "./DetailsContainer";
import PriceTransportCTAsContainer from "./PriceTransportCTAsContainer";
import InvoiceModalContent, {
  INVOICE_MODAL_TYPES,
} from "../InvoiceModal/InvoiceModalContent";
import TransportModalContent from "../TransportModal/TransportModalContent";
import { PaymentStatusEnum, Vehicle } from "../Interfaces/vehicle.type";
import buildRequestDeliveryLinkUrl from "../APICalls/buildRequestDeliveryLinkUrl";
import buildLinkUrl from "../APICalls/buildLinkUrl";
import getData from "../APICalls/getData";
import openInvoiceModalDataPush from "../AdobeDataPushes/invoiceModal/openInvoiceModalDataPush";
import openTransportModalDataPush from "../AdobeDataPushes/transportModal/openTransportModalDataPush";
import openDeliveryModalDataPush from "../AdobeDataPushes/transportModal/openDeliveryModalDataPush";
import { DisabledOverlay } from "../StyledComponents/DisabledOverlay";
import { storeType } from "../redux/store";
import { checkIsEligibleForDelivery } from "../Utilities/checkIsEligibleForDelivery";
import { MAX_SELECTED_VEHICLES } from "../constants";
import { useAuthZeroContext } from "../context/AuthZeroContext";

interface VehicleCardMobileProps {
  vehicle: Vehicle;
  degradedServices: string[];
  setGetOpenBankingLinkError: (shouldShowBanner: boolean) => void;
}

interface BorderedCardProps extends CardProps {
  isChecked: boolean;
}

export interface TransportModalData {
  isDeliveryModal: boolean;
  isEligibleForDelivery: boolean;
  isEligibleForCollection: boolean;
  collectionsLocation: string;
  isCentralOffsiteLocation: boolean;
  requestDeliveryLink: string;
  requestDeliveryExportLink?: string;
}

const BorderedCard = styled(Card)<BorderedCardProps>`
  z-index: 0;
  position: relative;
  ${(props) =>
    css({
      border: props.isChecked && "0.5px solid",
      borderColor: props.isChecked && "rgba(33, 150, 243, 0.57)",
      boxShadow:
        props.isChecked &&
        "1px 1px 12px rgba(33, 150, 243, 0.12), 0px 10px 15px rgba(33, 150, 243, 0.07), 0px -4px 16px rgba(233, 245, 254, 0.03)",
      "&:hover": {
        boxShadow:
          props.isChecked &&
          "0px 32px 48px rgba(33, 150, 243, 0.12), 0px 10px 15px rgba(33, 150, 243, 0.07), 0px -4px 16px rgba(233, 245, 254, 0.03)",
      },
    })}
`;

const VehicleCardMobile: React.FunctionComponent<VehicleCardMobileProps> = ({
  vehicle,
  degradedServices,
  setGetOpenBankingLinkError,
}) => {
  const store = useSelector((state: storeType) => ({
    selectedForDelivery: state.bookDelivery.selectedForDelivery,
  }));

  const isEligibleForDelivery = checkIsEligibleForDelivery(vehicle);

  const isEligibleForCollection =
    vehicle?.invoice?.invoicePaid === true &&
    vehicle.isBookedForCollection === false &&
    vehicle.isOrderReferenceAdded === false;

  const isChecked = store.selectedForDelivery?.some(
    (storeVehicle: Vehicle) => storeVehicle.vrm === vehicle.vrm
  );

  const isDisabled =
    (Boolean(store.selectedForDelivery.length) && !isEligibleForDelivery) ||
    (store.selectedForDelivery.length >= MAX_SELECTED_VEHICLES &&
      !store.selectedForDelivery.some(
        (storeVehicle) => storeVehicle.vrm === vehicle.vrm
      ));

  const [transportModalVisible, setTransportModalVisible] =
    React.useState(false);
  const [transportModalData, setTransportModalData] =
    React.useState<TransportModalData | null>(null);

  const closeTransportModal = () => setTransportModalVisible(false);

  const openTransportModal = () => {
    openTransportModalDataPush(vehicle);
    setTransportModalData({
      isDeliveryModal: false,
      isEligibleForDelivery,
      isEligibleForCollection,
      collectionsLocation: vehicle.location,
      isCentralOffsiteLocation: vehicle.isCentralOffsiteLocation,
      requestDeliveryLink: buildRequestDeliveryLinkUrl({
        vehicleIdentifier: vehicle.vehicleIdentifier,
        saleCode: vehicle.saleCode,
      }),
    });
    setTransportModalVisible(true);
  };

  const openDeliveryModal = () => {
    openDeliveryModalDataPush(vehicle);
    setTransportModalData({
      isDeliveryModal: true,
      isEligibleForDelivery,
      isEligibleForCollection,
      collectionsLocation: vehicle.location,
      isCentralOffsiteLocation: vehicle.isCentralOffsiteLocation,
      requestDeliveryLink: buildRequestDeliveryLinkUrl({
        vehicleIdentifier: vehicle.vehicleIdentifier,
        saleCode: vehicle.saleCode,
      }),
      requestDeliveryExportLink: buildRequestDeliveryLinkUrl(
        {
          vehicleIdentifier: vehicle.vehicleIdentifier,
          saleCode: vehicle.saleCode,
        },
        true
      ),
    });
    setTransportModalVisible(true);
  };

  const [invoiceModalData, setInvoiceModalData] = React.useState(null);
  const [invoiceModalLoading, setInvoiceModalLoading] = React.useState(false);

  const closeInvoiceModal = async () => {
    setInvoiceModalData(null);
    setInvoiceModalLoading(false);
  };

  const {
    data: { isToUseAuthZero, authZeroAccessToken },
  } = useAuthZeroContext();

  const openInvoiceModal = async () => {
    setInvoiceModalLoading(true);
    let invoiceType;
    let openBankingLinkUrl = null;

    if (
      vehicle.paymentStatus === PaymentStatusEnum.Failed &&
      vehicle?.invoice?.invoicePaid === false
    ) {
      const getOpenBankingLinkUrl = buildLinkUrl(
        vehicle?.invoice?.generateOpenBankingPaymentLink,
        isToUseAuthZero
      );
      openBankingLinkUrl = await getData(
        getOpenBankingLinkUrl,
        authZeroAccessToken,
        isToUseAuthZero
      );
      invoiceType = INVOICE_MODAL_TYPES.PAYMENT_FAILED.case;
    }

    if (
      vehicle.paymentStatus === PaymentStatusEnum.Processing &&
      vehicle?.invoice?.invoicePaid === false
    ) {
      invoiceType = INVOICE_MODAL_TYPES.PAYMENT_PROCESSING.case;
    }

    if (vehicle.invoice?.invoicePaid === true) {
      invoiceType = INVOICE_MODAL_TYPES.INVOICE_PAID.case;
    }

    if (
      vehicle.paymentStatus === PaymentStatusEnum.InvoiceNotPaid &&
      vehicle.invoice?.invoicePaid === false
    ) {
      const getOpenBankingLinkUrl = buildLinkUrl(
        vehicle.invoice?.generateOpenBankingPaymentLink,
        isToUseAuthZero
      );
      openBankingLinkUrl = await getData(
        getOpenBankingLinkUrl,
        authZeroAccessToken,
        isToUseAuthZero
      );
      invoiceType = INVOICE_MODAL_TYPES.PAYMENT_DUE.case;
    }
    const getInvoiceLinkUrl = buildLinkUrl(
      vehicle.invoice?.invoiceBreakdownUrl,
      isToUseAuthZero
    );
    try {
      const invoiceData = await getData(
        getInvoiceLinkUrl,
        authZeroAccessToken,
        isToUseAuthZero
      );
      openInvoiceModalDataPush(vehicle, invoiceType);
      setInvoiceModalData({
        invoiceData,
        invoiceType,
        openBankingLinkUrl,
      });
      setInvoiceModalLoading(false);
    } catch (error) {
      setGetOpenBankingLinkError(true);
      setInvoiceModalLoading(false);
    }
  };

  const invoiceModalVisible = !(invoiceModalData === null);
  const cardVariant = isChecked || isDisabled ? "default" : "hoverable";

  return (
    <BorderedCard
      width="100%"
      height="auto"
      marginX={0}
      marginY={0}
      paddingX={0}
      paddingY={0}
      aria-label="vehicle card"
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
      isChecked={isChecked}
      variant={cardVariant}
    >
      {isDisabled && <DisabledOverlay />}
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        height="100%"
        justifyContent="space-between"
      >
        {invoiceModalVisible && (
          <FlyoutModal
            aria-label="invoice-modal"
            isDismissable
            isOpen={invoiceModalVisible}
            onClose={closeInvoiceModal}
            padding="0px"
            role="dialog"
          >
            <InvoiceModalContent
              vehicleData={vehicle}
              invoiceData={invoiceModalData.invoiceData}
              invoiceType={invoiceModalData.invoiceType}
              openBankingLinkUrl={invoiceModalData.openBankingLinkUrl}
              onClose={closeInvoiceModal}
            />
          </FlyoutModal>
        )}
        {transportModalVisible && (
          <FlyoutModal
            aria-label="transport-modal"
            isDismissable
            isOpen={transportModalVisible}
            onClose={closeTransportModal}
            padding="0px"
            role="dialog"
            css="
                @media only screen and (min-width: 1024px) {
                  width:600px;
                  max-width:66% ;
                }"
          >
            <TransportModalContent
              vehicle={vehicle}
              degradedServices={degradedServices}
              onClose={closeTransportModal}
              transportModalData={transportModalData}
              openInvoiceModal={openInvoiceModal}
            />
          </FlyoutModal>
        )}
        <Box display="flex" flexDirection="column" width="100%">
          <ImageContainer
            vehicle={vehicle}
            isChecked={isChecked}
            degradedServices={degradedServices}
          />
          <DetailsContainer
            vehicle={vehicle}
            degradedServices={degradedServices}
          />
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          height={137}
          alignSelf="flex-end"
        >
          <PriceTransportCTAsContainer
            vehicle={vehicle}
            isChecked={isChecked}
            isDisabled={isDisabled}
            degradedServices={degradedServices}
            openTransportModal={openTransportModal}
            openDeliveryModal={openDeliveryModal}
            openInvoiceModal={openInvoiceModal}
            invoiceModalLoading={invoiceModalLoading}
          />
        </Box>
      </Box>
    </BorderedCard>
  );
};

export default VehicleCardMobile;
